import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const EmailLink: React.FC = () => {
  const location = useLocation();
  const [emailProvider, setEmailProvider] = useState<string>('');
  const [isLinking, setIsLinking] = useState<boolean>(false);
  const [linkStatus, setLinkStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [hoveredProvider, setHoveredProvider] = useState<string | null>(null);

  // Simulate email connection when coming from dashboard or client scores
  useEffect(() => {
    if (
      location.pathname.includes('client-scores') ||
      location.pathname.includes('dashboard')
    ) {
      setIsConnected(true);
      setEmailProvider('gmail');
    }
  }, [location.pathname]);

  const handleProviderSelect = (provider: string) => {
    if (isConnected && provider === emailProvider) {
      return; // Don't change if already connected
    }
    setEmailProvider(provider);
    // Reset status when changing provider
    setLinkStatus(null);
  };

  const handleLinkEmail = async () => {
    setIsLinking(true);
    setLinkStatus(null);

    try {
      // This would be replaced with actual API call
      // const response = await api.linkEmail(emailProvider);

      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Mock success response
      setLinkStatus({
        success: true,
        message: `Your ${emailProvider} account has been successfully linked!`,
      });

      // Set as connected after successful linking
      setIsConnected(true);
    } catch (error) {
      setLinkStatus({
        success: false,
        message:
          'An error occurred while linking your email account. Please try again.',
      });
    } finally {
      setIsLinking(false);
    }
  };

  const handleDisconnect = () => {
    // Simulate disconnection
    setIsConnected(false);
    setLinkStatus({
      success: true,
      message: `Your ${emailProvider} account has been disconnected.`,
    });
  };

  return (
    <div className="max-w-3xl mx-auto py-8">
      <h1 className="text-3xl font-bold mb-6 text-center">
        Link Your Email Account
      </h1>
      <p className="text-lg mb-8 text-center">
        Connect your email account to start analyzing potential real estate
        clients.
      </p>

      <div className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Select Email Provider</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div
            className="relative"
            onMouseEnter={() =>
              isConnected &&
              emailProvider === 'gmail' &&
              setHoveredProvider('gmail')
            }
            onMouseLeave={() => setHoveredProvider(null)}>
            <button
              className={`p-4 border rounded-lg flex items-center justify-center transition-colors w-full
                ${
                  emailProvider === 'gmail' && !isConnected
                    ? 'border-blue-500 bg-blue-50'
                    : isConnected && emailProvider === 'gmail'
                    ? 'border-green-500 bg-green-50'
                    : 'hover:bg-gray-50'
                }`}
              onClick={() => handleProviderSelect('gmail')}>
              <span className="text-lg font-medium flex items-center">
                Gmail
                {isConnected && emailProvider === 'gmail' && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-green-600 ml-2"
                    viewBox="0 0 20 20"
                    fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </span>
            </button>
            {hoveredProvider === 'gmail' && (
              <div className="absolute inset-0 flex items-center justify-center bg-red-500 bg-opacity-80 rounded-lg transition-opacity duration-200">
                <button
                  className="text-white font-medium flex items-center"
                  onClick={handleDisconnect}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-1"
                    viewBox="0 0 20 20"
                    fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Disconnect
                </button>
              </div>
            )}
          </div>

          <button
            className={`p-4 border rounded-lg flex items-center justify-center transition-colors ${
              emailProvider === 'microsoft' && !isConnected
                ? 'border-blue-500 bg-blue-50'
                : 'hover:bg-gray-50'
            }`}
            onClick={() => handleProviderSelect('microsoft')}>
            <span className="text-lg font-medium">Microsoft</span>
          </button>
          <button
            className={`p-4 border rounded-lg flex items-center justify-center transition-colors ${
              emailProvider === 'zoho' && !isConnected
                ? 'border-blue-500 bg-blue-50'
                : 'hover:bg-gray-50'
            }`}
            onClick={() => handleProviderSelect('zoho')}>
            <span className="text-lg font-medium">Zoho</span>
          </button>
          <button
            className={`p-4 border rounded-lg flex items-center justify-center transition-colors ${
              emailProvider === 'other' && !isConnected
                ? 'border-blue-500 bg-blue-50'
                : 'hover:bg-gray-50'
            }`}
            onClick={() => handleProviderSelect('other')}>
            <span className="text-lg font-medium">Other</span>
          </button>
        </div>

        {emailProvider && !isConnected && (
          <div className="mt-6">
            <button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg disabled:bg-blue-300"
              onClick={handleLinkEmail}
              disabled={isLinking}>
              {isLinking
                ? 'Linking...'
                : `Link ${
                    emailProvider.charAt(0).toUpperCase() +
                    emailProvider.slice(1)
                  } Account`}
            </button>
          </div>
        )}

        {isConnected && (
          <div className="mt-6">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center text-green-700">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="font-medium">
                  Your Gmail account is connected!
                </span>
              </div>
              <p className="mt-2 text-sm text-green-600">
                We're now analyzing your emails for potential real estate
                clients. Check the Client Scores page to see results.
              </p>
            </div>
          </div>
        )}

        {linkStatus && !isConnected && (
          <div
            className={`mt-4 p-4 rounded-lg ${
              linkStatus.success
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
            {linkStatus.message}
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-gray-200">
          <div className="bg-blue-50 rounded-lg p-5">
            <h3 className="font-bold text-blue-800 mb-3 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              Why we need access to your email:
            </h3>
            <ul className="space-y-2 text-blue-700">
              <li className="flex items-start">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2 text-blue-600 mt-0.5 flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                To analyze potential real estate client emails
              </li>
              <li className="flex items-start">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2 text-blue-600 mt-0.5 flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                To score leads based on AI analysis
              </li>
              <li className="flex items-start">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2 text-blue-600 mt-0.5 flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                To help you prioritize your most promising clients
              </li>
            </ul>
            <div className="mt-4 p-3 bg-white rounded border border-blue-200 flex items-center text-sm text-gray-700">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2 text-blue-500"
                viewBox="0 0 20 20"
                fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
              We will never use your data for any other purpose or share it with
              third parties.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailLink;
